/**
 * Idempotently injects VibeDrift's context.md content into an AI-rules file
 * (CLAUDE.md by default) inside a managed, clearly-delimited block. Re-running
 * replaces the block in place — it never duplicates or corrupts surrounding text.
 */

import { readFile, writeFile } from "fs/promises";
import { join } from "path";

export const MANAGED_BLOCK_START = "<!-- vibedrift:context:start (auto-generated, do not edit by hand) -->";
export const MANAGED_BLOCK_END = "<!-- vibedrift:context:end -->";

export function upsertManagedBlock(existing: string, blockBody: string): string {
  const block = `${MANAGED_BLOCK_START}\n${blockBody}\n${MANAGED_BLOCK_END}`;
  const startIdx = existing.indexOf(MANAGED_BLOCK_START);
  const endIdx = existing.indexOf(MANAGED_BLOCK_END);
  if (startIdx !== -1 && endIdx !== -1 && endIdx > startIdx) {
    const before = existing.slice(0, startIdx);
    const after = existing.slice(endIdx + MANAGED_BLOCK_END.length);
    return `${before}${block}${after}`;
  }
  const sep = existing.length === 0 || existing.endsWith("\n\n") ? "" : existing.endsWith("\n") ? "\n" : "\n\n";
  return `${existing}${sep}${block}\n`;
}

export async function injectContext(
  rootDir: string,
  contextMd: string,
  targets: string[] = ["CLAUDE.md"],
): Promise<string[]> {
  const body = `> The block below is VibeDrift's distilled view of this repo's dominant patterns and open drift. It is regenerated by \`vibedrift --write-context --inject-context\`. Read it before editing code here.\n\n${contextMd}`;
  const written: string[] = [];
  for (const target of targets) {
    const filePath = join(rootDir, target);
    let existing: string;
    try {
      existing = await readFile(filePath, "utf8");
    } catch (e: unknown) {
      if ((e as NodeJS.ErrnoException).code !== "ENOENT") throw e;
      existing = "";
    }
    await writeFile(filePath, upsertManagedBlock(existing, body));
    written.push(target);
  }
  return written;
}
